import { error, log, warn } from 'cc';
import Network, { ResponseDataType } from './Network';
import ErrorName from '../../ErrorHandler/ErrorName';
import Delay from '../../Toolkit/Utils/Delay';
import Cryptor from '../../Toolkit/Helper/Cryptor';

const CHECK_HEARTBEAT_TIMEOUT = 3500;

export default class NetworkEventSource extends Network {
    private _eventSource: EventSource = null;
    private readonly messageDispatcher: {
        [path: string]: [(data: any) => void, (reason?: any) => void][];
    } = {};

    private _stopHeartBeatFunc: () => void = null;

    constructor(
        protected url: string,
        protected readonly cryptor: Cryptor,
        private _events: string[] = [],
        public readonly heartbeatTimeout: number = CHECK_HEARTBEAT_TIMEOUT,
    ) {
        super(url, cryptor);
    }

    public get readyState() {
        return this._eventSource.readyState;
    }

    public get isConnectionOpened() {
        return this._eventSource.readyState === EventSource.OPEN;
    }

    public async reconnect(url: string): Promise<void> {
        this.emit(Network.EVENT_TYPE.START_RECONNECT);
        const closePromise = this._isEventSourceClosed() ? Promise.resolve() : Delay.event(Network.EVENT_TYPE.DISCONNECTED, this);
        this.close();
        await closePromise;
        this.url = url || this.url;
        await this.open();
        this.emit(Network.EVENT_TYPE.RECONNECTED);
    }

    public open() {
        try {
            this.emit(Network.EVENT_TYPE.START_CONNTECT);
            this._eventSource = new EventSource(this.url);
        } catch (err) {
            error(`EventSource connection error in open: ${err}`);
            this.emit(Network.EVENT_TYPE.ERROR, ErrorName.CONNECTION_FAILED);
            return Delay.forever();
        }
        const connectionPromise = new Promise<void>((resolve) => {
            this._eventSource.onopen = () => {
                log(`EventSource Connection start successfully: ${this.url}`);
                this.emit(Network.EVENT_TYPE.CONNECTED);
                resolve();
            };
            this._eventSource.onmessage = (event: MessageEvent) => {
                this._receiveEvent(event);
            };
            this._eventSource.onerror = () => {
                error(`EventSource Connection error in onerror: ${this.url}`);
                this.emit(Network.EVENT_TYPE.ERROR, ErrorName.CONNECTION_FAILED);
                this.close();
            };
            this._events.forEach((path) => {
                this._eventSource.addEventListener(path, (event: MessageEvent) => {
                    this._receiveEvent(event, path);
                });
            });
        });
        return connectionPromise;
    }

    public async send(): Promise<any> {
        throw new Error('not allowed send data');
    }

    public close() {
        if (this._isEventSourceClosed()) {
            return;
        }
        log(`SSE Connection closed: ${this.url}`);
        this._eventSource.close();
        this.emit(Network.EVENT_TYPE.DISCONNECTED);
    }

    public startHeartBeat(path: string) {
        log(`SSE Start heart beat on path: ${path}`);
        this._startHeartBeat(path);
    }

    public stopHeartBeat() {
        log('SSE Stop heart beat');
        if (this._stopHeartBeatFunc) {
            this._stopHeartBeatFunc();
            this._stopHeartBeatFunc = null;
        }
    }

    private _receiveEvent(event: MessageEvent, path: string = 'message') {
        const receiveData = JSON.parse(event.data);
        const response = receiveData instanceof Object ? receiveData : JSON.parse(this.cryptor.decrypt(receiveData));
        let data: ResponseDataType['Data'] = response;
        if (response.Path === path) {
            data = response.Data;
        }
        // response
        this.messageDispatcher[path]?.shift()?.[0](data);
        // boardcast
        if (this.hasEventListener(path as string)) {
            this.emit(path as string, data);
        }
    }

    private _startHeartBeat(path: string) {
        if (!this.isConnectionOpened) {
            warn('SSE is not opened, cannot start heart beat');
            return;
        }

        this._eventSource.addEventListener(path, (event: MessageEvent) => {
            this.emit(path, event.data);
        });
        this._checkHeartBeat(path);
    }

    private async _checkHeartBeat(path: string) {
        try {
            await Promise.race([
                Delay.reject(this.heartbeatTimeout, ErrorName.TIMEOUT, true),
                Delay.event(path, this),
                new Promise((_, reject) => {
                    this._stopHeartBeatFunc = reject;
                }),
            ])
                .then(() => {
                    this._checkHeartBeat(path);
                })
                .catch((reason) => {
                    if (reason === ErrorName.TIMEOUT) {
                        warn(`SSE HeartBeat timeout, trying to reconnect: ${this.url}`);
                        this.close();
                        this.open();
                    }
                });
        } catch {
            log('SSE stop heart beating');
        }
    }

    private _isEventSourceClosed() {
        return !this._eventSource || this._eventSource.readyState === EventSource.CLOSED;
    }
}
